import type { ApiErrorBody } from "@mongmee/contracts";

type AuthenticationRequiredListener = (path: string) => void;

const authenticationRequiredListeners = new Set<AuthenticationRequiredListener>();

export function subscribeToAuthenticationRequired(
  listener: AuthenticationRequiredListener,
): () => void {
  authenticationRequiredListeners.add(listener);
  return () => authenticationRequiredListeners.delete(listener);
}

export class ApiError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  csrfToken?: string,
): Promise<T> {
  const method = options.method?.toUpperCase() ?? "GET";
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("x-csrf-token", csrfToken);
  }

  const response = await fetch(path, {
    ...options,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const body = await safeJson<ApiErrorBody>(response);
    if (response.status === 401 && body?.error.code === "AUTHENTICATION_REQUIRED") {
      for (const listener of authenticationRequiredListeners) listener(path);
    }
    throw new ApiError(
      body?.error.code ?? "REQUEST_FAILED",
      body?.error.message ?? `Request failed with status ${response.status}`,
      response.status,
      body?.error.requestId,
      body?.error.details,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}
